import type { OccupancySpanKind } from '@sea-snag/shared';

/**
 * What a grid cell prints where its price goes.
 *
 * Two different numbers can appear, and which one depends on whether the week
 * was sold: a booking shows what that booking is worth, an open week shows what
 * we are currently asking for it after discount.
 *
 * The third case is the one this exists for. A sold week with no price at all is
 * common — about a third of one operator's confirmed charters carry none, because
 * MMK records no price when a booking is created without a product attached. A
 * coloured cell containing nothing cannot be distinguished from one the app
 * failed to fill in, so it is marked instead.
 *
 * That mark is deliberately limited to weeks where money was expected. An option
 * or a service block without a price is unremarkable — a yard period is not a
 * sale — and marking those would put a dash on most of the off-season.
 */

/** Shown where a sold week has no price recorded. An en dash, not a hyphen. */
export const NO_PRICE_MARK = '–';

/**
 * Kinds that represent a week that was sold, and so should carry a price.
 * Exported so the cell detail can explain the mark with the same rule that
 * decides whether to show it.
 */
export const SOLD_SPAN_KINDS: ReadonlySet<OccupancySpanKind> = new Set<OccupancySpanKind>([
  'booked',
  'regatta',
  'owner',
]);

export interface CellPriceInput {
  /** The booking occupying this cell, if any. */
  span?: { kind: OccupancySpanKind; amount: number | null };
  /** The week's asking price, used only when no booking occupies the cell. */
  listPrice?: number | null;
  format: (amount: number) => string;
}

export function cellPriceLabel({ span, listPrice, format }: CellPriceInput): string {
  if (span) {
    if (span.amount != null) return format(span.amount);
    return SOLD_SPAN_KINDS.has(span.kind) ? NO_PRICE_MARK : '';
  }

  return listPrice != null ? format(listPrice) : '';
}
